// /api/me context — fetched once after sign-in and shared app-wide so the
// nav (admin item), case detail (manage actions) and admin page all gate on
// the same QcMe. null while loading or on failure; consumers treat null as
// "no manage affordances" (the API enforces server-side regardless).

import { createContext, useContext, useEffect, useState } from 'react';
import type { QcMe } from '@itms-qc/shared';
import { api } from './api';
import { useApiToken } from '../auth/useApiToken';

const MeContext = createContext<QcMe | null>(null);

export function MeProvider({ children }: { children: React.ReactNode }) {
  const getToken = useApiToken();
  const [me, setMe] = useState<QcMe | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const token = await getToken();
        const result = await api.me(token);
        if (!cancelled) setMe(result);
      } catch (err) {
        // Leave me=null — the UI degrades to view/file-only affordances.
        console.error('[me] failed to load /api/me:', err);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  return <MeContext.Provider value={me}>{children}</MeContext.Provider>;
}

/** QcMe for the signed-in user, or null while loading / on failure. */
export function useMe(): QcMe | null {
  return useContext(MeContext);
}
